import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Upload, 
  Image as ImageIcon, 
  X, 
  Eye, 
  Trash2,
  RefreshCw,
  AlertCircle,
  Check,
  Heart,
  Leaf,
  DollarSign,
  Type,
  Palette
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { deleteThemeImage, API_BASE_URL, apiClient, handleApiError } from '../../services/api';
import { toAbs } from '../../utils/url';

// Tipos padronizados
type ThemeKey = 'amor' | 'saude' | 'dinheiro';
type ThemeImagesMap = Record<ThemeKey, string | null>;
const THEME_KEYS: readonly ThemeKey[] = ['amor','saude','dinheiro'] as const;

function isThemeKey(v: string): v is ThemeKey {
  return (THEME_KEYS as readonly string[]).includes(v);
}

interface ThemeImage {
  id: number;
  theme_type: string;
  image_url: string;
  is_active: boolean;
}

interface ThemeImageManagerProps {
  onImageChange?: (themeType: string, imageUrl: string | null) => void;
  onTypographyChange?: (typography: ThemeBoxTypography) => void;
}

interface ThemeBoxTypography {
  fontSize: string;
  fontWeight: string;
  fontStyle: string;
  color: string;
}

const ThemeImageManager: React.FC<ThemeImageManagerProps> = ({ onImageChange, onTypographyChange }) => {
  const { token } = useAuth();
  const [themeImages, setThemeImages] = useState<ThemeImagesMap>({
    amor: null,
    saude: null,
    dinheiro: null
  });
  const [typography, setTypography] = useState<ThemeBoxTypography>({
    fontSize: '16px',
    fontWeight: 'normal',
    fontStyle: 'normal',
    color: '#FFFFFF'
  });
  const [customText, setCustomText] = useState('Texto de Exemplo');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingTheme, setUploadingTheme] = useState<string | null>(null);
  const [removingTheme, setRemovingTheme] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<'images' | 'typography'>('images');
  const fileInputRefs = {
    amor: useRef<HTMLInputElement>(null),
    saude: useRef<HTMLInputElement>(null),
    dinheiro: useRef<HTMLInputElement>(null)
  };

  const themes = [
    { id: 'amor', name: 'Amor', icon: Heart, color: 'from-pink-500 to-rose-500' },
    { id: 'saude', name: 'Saúde', icon: Leaf, color: 'from-green-500 to-emerald-500' },
    { id: 'dinheiro', name: 'Dinheiro', icon: DollarSign, color: 'from-yellow-500 to-orange-500' }
  ];

  useEffect(() => {
    loadThemeImages();
    loadTypographySettings();
  }, []);

  const loadTypographySettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/theme/configs/active`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.typography?.themeBoxTypography) {
          setTypography(data.typography.themeBoxTypography);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configurações de tipografia:', error);
    }
  };

  const loadThemeImages = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/theme-images/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        
        // Aceitamos tanto string quanto objeto { image_url } vindo do backend
        type IncomingImageValue = string | { image_url?: string | null } | null | undefined;
        type IncomingImages = Record<string, IncomingImageValue>;
        
        const incoming: IncomingImages = (data?.theme_images ?? {}) as IncomingImages;
        
        // Mapa base com as 3 chaves obrigatórias
        const base: ThemeImagesMap = { amor: null, saude: null, dinheiro: null };
        
        // Preenche apenas chaves válidas
        const imagesByTheme: ThemeImagesMap = { ...base };
        
        // Processa os dados vindos do backend
        if (Array.isArray(data.theme_images)) {
          data.theme_images.forEach((img: ThemeImage) => {
            if (isThemeKey(img.theme_type)) {
              imagesByTheme[img.theme_type] = img.image_url || null;
            }
          });
        }
        
        // Agora o objeto tem exatamente as chaves esperadas
        setThemeImages(imagesByTheme);
      }
    } catch (error) {
      console.error('Erro ao carregar imagens dos temas:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar imagens dos temas' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (themeType: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Formato não suportado. Use JPG, PNG ou WebP.' });
      return;
    }

    // Validar tamanho (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Arquivo muito grande. Máximo 5MB.' });
      return;
    }

    uploadImage(themeType, file);
  };

  const uploadImage = async (themeType: string, file: File) => {
    setUploadingTheme(themeType);
    try {
      if (!token) {
        setMessage({ type: 'error', text: 'Sessão expirada. Faça login novamente.' });
        return;
      }

      const formData = new FormData();
      formData.append('file', file);

      const { data } = await apiClient.post(`/theme-images/upload/${themeType}`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      setThemeImages(prev => ({
        ...prev,
        [themeType]: data?.theme_image?.image_url || null
      }));
      onImageChange?.(themeType, data?.theme_image?.image_url || null);
      setMessage({ type: 'success', text: `Imagem do tema ${themes.find(t => t.id === themeType)?.name} atualizada!` });
    } catch (error: any) {
      console.error('Erro no upload:', error);
      setMessage({ type: 'error', text: handleApiError(error) });
    } finally {
      setUploadingTheme(null);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const removeImage = async (themeType: string) => {
    try {
      setRemovingTheme(themeType);
      const data = await deleteThemeImage(themeType as "amor"|"saude"|"dinheiro");
      
      if (data?.ok && data?.images) {
        // Normalizador para aceitar tanto string quanto objeto { image_url }
        type IncomingImageValue = string | { image_url?: string | null } | null | undefined;
        type IncomingImages = Partial<Record<ThemeKey, IncomingImageValue>>;
        
        const images = (data.images ?? {}) as IncomingImages;
        
        const updatedImages: Partial<ThemeImagesMap> = {};
        
        (Object.entries(images) as [string, IncomingImageValue][])
          .forEach(([rawKey, rawVal]) => {
            const key = rawKey as ThemeKey;
            
            let url: string | null = null;
            if (typeof rawVal === 'string') {
              url = rawVal || null;
            } else if (rawVal && typeof rawVal === 'object' && 'image_url' in rawVal) {
              url = ((rawVal as any).image_url as string | null) || null;
            }
            
            updatedImages[key] = (url && url.trim().length > 0) ? url : null;
          });
        
        // Atualiza o state preservando o que já existe
        setThemeImages(prev => ({
          ...prev,
          ...(updatedImages as ThemeImagesMap),
        }));
        
        onImageChange?.(themeType, null);
        setMessage({ type: 'success', text: `Imagem do tema ${themes.find(t => t.id === themeType)?.name} removida!` });
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: 'Erro ao remover imagem' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      console.error('Erro ao remover imagem:', error);
      setMessage({ type: 'error', text: 'Erro ao remover imagem' });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setRemovingTheme(null);
    }
  };

  const handleTypographyChange = (field: keyof ThemeBoxTypography, value: string) => {
    const newTypography = { ...typography, [field]: value };
    setTypography(newTypography);
    onTypographyChange?.(newTypography);
  };

  const saveTypographySettings = async () => {
    try {
      // Primeiro, buscar a configuração ativa atual
      const activeResponse = await fetch(`${API_BASE_URL}/theme/configs/active`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (activeResponse.ok) {
        const activeConfig = await activeResponse.json();
        
        // Atualizar os campos específicos de tipografia das caixas de tema
        const updatedConfig = {
          ...activeConfig,
          theme_box_font_size: typography.fontSize,
          theme_box_font_weight: typography.fontWeight,
          theme_box_font_style: typography.fontStyle,
          theme_box_color: typography.color
        };

        // Salvar a configuração atualizada
        const updateResponse = await fetch(`${API_BASE_URL}/theme/configs/${activeConfig.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(updatedConfig),
        });

        if (updateResponse.ok) {
          setMessage({ type: 'success', text: 'Configurações de tipografia salvas com sucesso!' });
          setTimeout(() => setMessage(null), 3000);
          
          // Chamar o callback para atualizar o contexto (apenas estilos, não texto)
          onTypographyChange?.(typography);
        } else {
          throw new Error('Erro ao salvar configurações');
        }
      }
    } catch (error) {
      console.error('Erro ao salvar tipografia:', error);
      setMessage({ type: 'error', text: 'Erro ao salvar configurações de tipografia' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const getImageUrl = (imageUrl: string | null | undefined) => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    return toAbs(imageUrl);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white mb-2">Configurações de Tema</h3>
          <p className="text-purple-300 text-sm">
            Configure imagens de fundo e tipografia das caixas de seleção de tema
          </p>
        </div>
        <button
          onClick={loadThemeImages}
          disabled={isLoading}
          className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-purple-300 hover:text-white rounded-lg transition-all disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          <span>Atualizar</span>
        </button>
      </div>

      {/* Tabs de Navegação */}
      <div className="flex space-x-1 bg-white/10 p-1 rounded-lg">
        <button
          onClick={() => setActiveSection('images')}
          className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-all ${
            activeSection === 'images'
              ? 'bg-purple-600 text-white'
              : 'text-purple-300 hover:text-white hover:bg-white/10'
          }`}
        >
          <ImageIcon className="w-4 h-4" />
          <span>Imagens</span>
        </button>
        <button
          onClick={() => setActiveSection('typography')}
          className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-all ${
            activeSection === 'typography'
              ? 'bg-purple-600 text-white'
              : 'text-purple-300 hover:text-white hover:bg-white/10'
          }`}
        >
          <Type className="w-4 h-4" />
          <span>Tipografia</span>
        </button>
      </div>

      {/* Conteúdo baseado na aba ativa */}
      {activeSection === 'images' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {themes.map((theme) => {
            const Icon = theme.icon;
            const key = isThemeKey(theme.id) ? theme.id : null;
            const currentImage = key ? themeImages[key] : null;
            const isUploading = uploadingTheme === theme.id;

            return (
              <motion.div
                key={theme.id}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg bg-gradient-to-r ${theme.color}`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <h4 className="text-white font-semibold">{theme.name}</h4>
                  </div>
                </div>

                {/* Área de Upload/Preview */}
                <div className="relative">
                  {currentImage ? (
                    <div className="relative group">
                      <div className="aspect-video bg-gray-800 rounded-lg overflow-hidden">
                        <img
                          src={getImageUrl(currentImage)}
                          alt={`Imagem do tema ${theme.name}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center space-x-2">
                        <button
                          onClick={() => setPreviewImage(getImageUrl(currentImage))}
                          className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeImage(theme.id)}
                          disabled={removingTheme === theme.id}
                          className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                          {removingTheme === theme.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRefs[theme.id as keyof typeof fileInputRefs]?.current?.click()}
                      className="aspect-video border-2 border-dashed border-white/30 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-white/50 hover:bg-white/5 transition-all"
                    >
                      {isUploading ? (
                        <div className="flex flex-col items-center space-y-2">
                          <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
                          <span className="text-purple-300 text-sm">Enviando...</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center space-y-2">
                          <Upload className="w-8 h-8 text-purple-400" />
                          <span className="text-purple-300 text-sm text-center">
                            Clique para enviar<br />imagem de fundo
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <input
                    ref={fileInputRefs[theme.id as keyof typeof fileInputRefs]}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={(e) => handleFileSelect(theme.id, e)}
                    className="hidden"
                  />
                </div>

                {/* Botão de Upload quando há imagem */}
                {currentImage && (
                  <button
                    onClick={() => fileInputRefs[theme.id as keyof typeof fileInputRefs]?.current?.click()}
                    disabled={isUploading}
                    className="w-full mt-4 flex items-center justify-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Upload className="w-4 h-4" />
                    <span>Alterar Imagem</span>
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Seção de Tipografia */}
      {activeSection === 'typography' && (
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500">
                <Type className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="text-white font-semibold">Tipografia das Caixas de Tema</h4>
                <p className="text-purple-300 text-sm">Configure a aparência do texto nas caixas Amor, Saúde e Dinheiro</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Tamanho da Fonte */}
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                Tamanho da Fonte
              </label>
              <select
                value={typography.fontSize}
                onChange={(e) => handleTypographyChange('fontSize', e.target.value)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="12px" className="bg-gray-800">12px</option>
                <option value="14px" className="bg-gray-800">14px</option>
                <option value="16px" className="bg-gray-800">16px</option>
                <option value="18px" className="bg-gray-800">18px</option>
                <option value="20px" className="bg-gray-800">20px</option>
                <option value="24px" className="bg-gray-800">24px</option>
                <option value="28px" className="bg-gray-800">28px</option>
                <option value="32px" className="bg-gray-800">32px</option>
              </select>
            </div>

            {/* Peso da Fonte */}
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                Peso da Fonte
              </label>
              <select
                value={typography.fontWeight}
                onChange={(e) => handleTypographyChange('fontWeight', e.target.value)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="normal" className="bg-gray-800">Normal</option>
                <option value="bold" className="bg-gray-800">Negrito</option>
                <option value="100" className="bg-gray-800">Thin (100)</option>
                <option value="200" className="bg-gray-800">Extra Light (200)</option>
                <option value="300" className="bg-gray-800">Light (300)</option>
                <option value="400" className="bg-gray-800">Regular (400)</option>
                <option value="500" className="bg-gray-800">Medium (500)</option>
                <option value="600" className="bg-gray-800">Semi Bold (600)</option>
                <option value="700" className="bg-gray-800">Bold (700)</option>
                <option value="800" className="bg-gray-800">Extra Bold (800)</option>
                <option value="900" className="bg-gray-800">Black (900)</option>
              </select>
            </div>

            {/* Estilo da Fonte */}
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                Estilo da Fonte
              </label>
              <select
                value={typography.fontStyle}
                onChange={(e) => handleTypographyChange('fontStyle', e.target.value)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="normal" className="bg-gray-800">Normal</option>
                <option value="italic" className="bg-gray-800">Itálico</option>
              </select>
            </div>

            {/* Cor da Fonte */}
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                Cor da Fonte
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={typography.color}
                  onChange={(e) => handleTypographyChange('color', e.target.value)}
                  className="w-12 h-10 rounded border border-white/20 bg-transparent cursor-pointer"
                />
                <input
                  type="text"
                  value={typography.color}
                  onChange={(e) => handleTypographyChange('color', e.target.value)}
                  className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="#FFFFFF"
                />
              </div>
            </div>

            {/* Texto Personalizado para Preview */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-purple-200 mb-2">
                Texto para Preview
              </label>
              <input
                type="text"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Digite o texto que deseja visualizar no preview"
              />
            </div>
          </div>

          {/* Preview */}
          <div className="mt-6 p-4 bg-white/5 rounded-lg border border-white/10">
            <h5 className="text-purple-200 text-sm font-medium mb-3">Preview:</h5>
            <div className="flex space-x-4">
              {themes.map((theme) => {
                const Icon = theme.icon;
                return (
                  <div
                    key={theme.id}
                    className={`flex-1 p-4 rounded-lg bg-gradient-to-r ${theme.color} flex items-center justify-center space-x-2`}
                  >
                    <Icon className="w-5 h-5 text-white" />
                    <span
                      style={{
                        fontSize: typography.fontSize,
                        fontWeight: typography.fontWeight,
                        fontStyle: typography.fontStyle,
                        color: typography.color
                      }}
                    >
                      {customText}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Botão Salvar */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={saveTypographySettings}
              className="flex items-center space-x-2 px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              <Palette className="w-4 h-4" />
              <span>Salvar Configurações</span>
            </button>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-4 -right-4 p-2 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors z-10"
            >
              <X className="w-4 h-4" />
            </button>
            <img
              src={previewImage}
              alt="Preview"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      {/* Mensagem de Status */}
      {message && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className={`flex items-center space-x-2 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-500/20 border border-green-500/30 text-green-300'
              : 'bg-red-500/20 border border-red-500/30 text-red-300'
          }`}
        >
          {message.type === 'success' ? (
            <Check className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span>{message.text}</span>
        </motion.div>
      )}
    </div>
  );
};

export default ThemeImageManager;